import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  doCreateClass,
  doUpdateClass,
  doUpdateClassInfo,
} from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import { useSnackbar } from 'notistack';
import {
  warningVariant,
  errorVariant,
  successVariant,
} from '../../../lib/barVariant';
import Button from '../../Button';
import ClassBasicInfo from './BasicInfo';
import ClassLessonInfo from './LessonInfo';
import ClassEditor from './Editor';
import ConfirmModal from '../../modals/ConfirmModal';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import {closeClass, updateClass2, updateClassInfo2, updateClassSubject} from '../../../api/campusClasses.api';
import useNotification from '../../../hooks/useNotification';

interface IProps {
  tab: string;
  setTab: (tab: string, classCode?: string) => void;
  doGetClassData: (classCode: string) => void;
  setRefreshTable: any;
}

const ClassInfo: FC<IProps> = ({
  tab,
  setTab,
  doGetClassData,
  setRefreshTable,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { successNotification } = useNotification();
  const {
    data: closeRes,
    isLoading: closeIsLoading,
    error: closeError,
    execute: closeExecute,
  } = useAxios();
  const {
    data: updateRes,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios();

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  useEffect(() => {
    if (closeRes && !closeIsLoading) {
      if (chosenClass) doGetClassData(chosenClass.classCode);
      successNotification(t('class.class.message1'));
    }
  }, [closeRes, closeIsLoading]);

  const createClass = async (campusCode: string, req: any) => {
    const res = await dispatch(doCreateClass(campusCode, req));

    switch (res?.status) {
      case 200:
        // 등록 완료 시 상세 화면으로
        const data = res?.data?.data;
        setTab('info', data.classCode);
        setRefreshTable((current: boolean) => !current);
        enqueueSnackbar(t('common.message.create1'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const updateClass = async (type: number, classCode: string, req: any) => {
    if (type === 1) {
      updateExecute(updateClass2(classCode, req));
    } else if (type === 2) {
      updateExecute(updateClassInfo2(classCode, req));
    } else {
      updateExecute(updateClassSubject(classCode, req));
    }
  };

  const onCloseClass = () => {
    if (!chosenClass) return;
    closeExecute(closeClass({ classCode: chosenClass.classCode }));
  };

  const renderTabContent = () => {
    switch (tab) {
      case 'info':
        return (
          <>
            <ClassBasicInfo data={chosenClass} />
            <ClassLessonInfo data={chosenClass} />
            <div className="text-right mt-3">
              <Button
                className="btn btn-sm btn-secondary btn-w100p mr-1"
                onClick={() => setConfirmCloseVisible(true)}
                text={t('class.class.column9')}
                writeAuthYn={writeAuthYn}
              />
              <Button
                className="btn btn-sm btn-primary btn-w100p"
                onClick={() => setTab('updateClass')}
                text={t('common.button.edit')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </>
        );
      case 'createClass':
        return <ClassEditor submit={createClass} />;
      case 'updateClass':
        return <ClassEditor classData={chosenClass} submit={updateClass} />;
    }
  };

  useEffect(() => {
    if (updateRes) {
      if (chosenClass) doGetClassData(chosenClass.classCode);
      setTab('info');
      setRefreshTable((current: boolean) => !current);
      successNotification(t('common.message.save1'));
    }
  }, [updateRes]);

  return (
    <>
      {(closeIsLoading || updateIsLoading) && <Loader />}
      <ErrorResponse error={updateError} />
      <ErrorResponse error={closeError} />
      {renderTabContent()}

      {confirmCloseVisible &&
        <ConfirmModal
          visible={confirmCloseVisible}
          setVisible={setConfirmCloseVisible}
          onConfirm={onCloseClass}
          titleText={t('class.class.title4')}
          messageText={t('class.class.text9')}
          confirmText={t('class.class.column9')}
          cancelText={t('common.button.cancel')}
        />
      }
    </>
  );
}

export default ClassInfo;
